/**
 * Tap-vs-scroll guard for touchend-driven "click" handlers.
 *
 * Several components fire their action from `onTouchEnd` (to dodge iOS
 * Safari's swallowed-first-tap problem). But `touchend` also fires at the
 * end of a SCROLL gesture — native browsers only synthesize a click when
 * the finger didn't move, and handlers that skip that distinction turn
 * scrolling into accidental taps on everything the finger crosses.
 *
 * One capture-phase, passive listener pair tracks whether the current
 * touch sequence moved beyond a small slop. Handlers call `wasTouchTap()`
 * first and bail when it returns false — the browser then treats the
 * gesture as the scroll it was (a moved touch never produces a native
 * click, so bailing fires nothing).
 */

const TAP_SLOP_PX = 10;

let startX = 0;
let startY = 0;
let moved = false;

if (typeof window !== "undefined") {
  window.addEventListener(
    "touchstart",
    (e) => {
      const t = e.touches[0];
      if (!t) return;
      startX = t.clientX;
      startY = t.clientY;
      moved = false;
    },
    { capture: true, passive: true },
  );
  window.addEventListener(
    "touchmove",
    (e) => {
      if (moved) return;
      const t = e.touches[0];
      if (!t) return;
      if (
        Math.abs(t.clientX - startX) > TAP_SLOP_PX ||
        Math.abs(t.clientY - startY) > TAP_SLOP_PX
      ) {
        moved = true;
      }
    },
    { capture: true, passive: true },
  );
}

/** True when the touch sequence ending now was a tap, not a scroll/drag. */
export function wasTouchTap(): boolean {
  return !moved;
}
